import { useState, useEffect } from 'react'

import {
  MassActionRequest,
  MassActionRequestStatus,
} from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useMassActionRequest,
  useMassActionRequestInProgress,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'

import { MassActionAlertStatus, MassActionTitleStatus } from './constants.js'

const Banner = ({
  massActionRequest,
}: {
  massActionRequest?: MassActionRequest
}) => {
  const [hasFinished, setHasFinished] = useState(false)
  const { $t } = useI18n()

  const { data: massActionData } = useMassActionRequest({
    variables: {
      id: massActionRequest?.id,
    },
    useQueryOptions: {
      enabled: !hasFinished,
      refetchInterval: 5000,
    },
  })

  useEffect(() => {
    setHasFinished(massActionData?.status === MassActionRequestStatus.Done)
  }, [massActionData?.status])

  const updatedMassActionData = massActionData ?? massActionRequest

  return (
    <Alert
      status={MassActionAlertStatus(updatedMassActionData)}
      title={MassActionTitleStatus($t, updatedMassActionData)}
      className="shadow"
    >
      {updatedMassActionData?.processedCount > 0 &&
        updatedMassActionData?.processedCount <
          updatedMassActionData?.entitiesCount && (
          <div>
            <T
              defaultMessage="Processing {processedCount} out of {entitiesCount}."
              id="MassAction.ProcessingCount"
              values={{
                processedCount: updatedMassActionData?.processedCount,
                entitiesCount: updatedMassActionData?.entitiesCount,
              }}
            />
          </div>
        )}
      {updatedMassActionData?.status === MassActionRequestStatus.Done && (
        <div>
          <T
            defaultMessage="Successful: {successCount} out of {entitiesCount}."
            id="MassAction.SuccessCount"
            values={{
              successCount:
                (updatedMassActionData?.entitiesCount || 0) -
                (updatedMassActionData?.failedCount || 0),
              entitiesCount: updatedMassActionData?.entitiesCount,
            }}
          />
        </div>
      )}
      {updatedMassActionData?.failedCount > 0 && (
        <div>
          <T
            defaultMessage="Failed: {failedCount}"
            id="MassAction.FailedCount"
            values={{
              failedCount: updatedMassActionData?.failedCount,
            }}
          />
        </div>
      )}
    </Alert>
  )
}

export const MassActionBanner = ({
  massActionRequest,
}: {
  massActionRequest?: MassActionRequest
}) => {
  const { data: inProgressData } = useMassActionRequestInProgress({
    useQueryOptions: {
      enabled: !massActionRequest?.id,
      retry: false,
    },
  })

  const massAction = massActionRequest ?? inProgressData

  if (!massAction) {
    return null
  }

  return <Banner massActionRequest={massAction} key={massAction.id} />
}
